import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CheckCheck, Trash2, X, Settings } from "lucide-react";


/** Bell icon with unread badge + dropdown panel. */
export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      // Silent by design — user may not be logged in yet; log at debug for troubleshooting.
      console.debug("[notif-bell] load skipped", err?.response?.status || err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);  // poll every 30s
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (open && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    setItems(items.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnread(Math.max(0, unread - 1));
    try { await api.post(`/notifications/${id}/read`); } catch { load(); }
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All notifications marked as read");
      load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    setItems(items.filter(n => n.notification_id !== id));
    try { await api.delete(`/notifications/${id}`); } catch { load(); }
  };

  return (
    <div ref={panelRef} className="relative">
      <button data-testid="notif-bell-btn"
        onClick={()=>{ setOpen(!open); if (!open) load(); }}
        className="relative p-2 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">
        <Bell size={15}/>
        {unread > 0 && (
          <span data-testid="notif-unread-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-mono font-bold
              bg-[var(--accent)] text-black flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel"
          className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] card-sharp shadow-2xl z-50 overflow-hidden">
          <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-black/40">
            <Bell size={13} className="text-[var(--accent)]"/>
            <h4 className="font-display text-sm text-white flex-1">Notifications</h4>
            {unread > 0 && (
              <button data-testid="notif-mark-all" onClick={markAllRead}
                className="text-[11px] font-mono text-[var(--accent)] hover:underline flex items-center gap-1">
                <CheckCheck size={11}/> Mark all
              </button>
            )}
            <Link to="/settings/notifications" onClick={()=>setOpen(false)}
              data-testid="notif-settings-link"
              className="text-[11px] font-mono text-[var(--text-muted)] hover:text-white flex items-center gap-1">
              <Settings size={11}/>
            </Link>
          </header>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center" data-testid="notif-empty">
                <Bell size={28} className="mx-auto text-[var(--text-muted)] mb-2"/>
                <p className="text-xs font-mono text-[var(--text-muted)]">You're all caught up.</p>
              </div>
            ) : (
              items.map((n) => (
                <NotifRow key={n.notification_id} n={n}
                  onRead={()=>markRead(n.notification_id)}
                  onDelete={()=>del(n.notification_id)}
                  onNav={()=>setOpen(false)}/>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function NotifRow({ n, onRead, onDelete, onNav }) {
  const iconColor = n.kind === "trade_entered" ? "text-[var(--accent)]"
    : n.kind === "trade_closed" ? (n.data?.pnl_usd >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]")
    : n.kind === "deployment_error" ? "text-[var(--loss)]"
    : n.kind === "emotion_pattern" ? "text-[var(--warning)]"
    : "text-[var(--text-secondary)]";
  const when = timeAgo(n.created_at);
  const Body = (
    <>
      <div className={`w-1 self-stretch ${n.read ? "bg-transparent" : "bg-[var(--accent)]"}`}/>
      <div className={`px-3 py-2.5 flex-1 min-w-0 ${n.read ? "opacity-70" : ""}`}>
        <div className="flex items-start gap-2">
          <div className={`font-display text-sm truncate flex-1 ${iconColor}`}>{n.title}</div>
          <span className="text-[10px] font-mono text-[var(--text-muted)] whitespace-nowrap">{when}</span>
        </div>
        <p className="text-xs font-mono text-[var(--text-secondary)] mt-1 line-clamp-2">{n.body}</p>
      </div>
    </>
  );
  return (
    <div data-testid={`notif-row-${n.notification_id}`}
      className="border-b border-white/5 flex items-stretch group hover:bg-white/[.03]">
      {n.url ? (
        <Link to={n.url} className="flex items-stretch flex-1 min-w-0"
          onClick={() => { onRead(); onNav?.(); }}>
          {Body}
        </Link>
      ) : (
        <button className="flex items-stretch flex-1 min-w-0 text-left" onClick={onRead}>
          {Body}
        </button>
      )}
      <button data-testid={`notif-del-${n.notification_id}`}
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 px-2 text-[var(--text-muted)] hover:text-[var(--loss)] transition-all">
        <X size={12}/>
      </button>
    </div>
  );
}


function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
}
